/**
 * Operational schedule — the inline "simple" schedule only. Vendored (by copy, adapted to 24h
 * "HH:mm" inputs) from scio3 `edit-screen/ops-schedule/ops-schedule-utils.ts` (`buildSimpleRRule`,
 * `parseSimpleRRule`, `formatSimpleScheduleText`) and `DeviceOpsScheduleField.opsScheduleFeaturePatch`.
 *
 * Simple mode writes two strings straight onto `device.feature`:
 *  - `scheduleOperationalRRule` = `DTSTART:20240303T{HH}{mm}00Z\nRRULE:FREQ=WEEKLY;BYDAY=…;INTERVAL=1`
 *    (DTSTART's time-of-day IS the device's ON gate, so it must equal the range start — scio3 #1940);
 *  - `scheduleOperationalRange` = `HH:mm;HH:mm`.
 * Creating NEW named Ops schedules (scio3 "+ New" → saveSchedule) is out of scope here.
 */

/** Sunday-first, matching RRULE BYDAY order and scio3's day values 0–6. */
export const WEEK_DAYS = [
  { value: 0, byDay: 'SU', short: 'S', name: 'Sunday' },
  { value: 1, byDay: 'MO', short: 'M', name: 'Monday' },
  { value: 2, byDay: 'TU', short: 'T', name: 'Tuesday' },
  { value: 3, byDay: 'WE', short: 'W', name: 'Wednesday' },
  { value: 4, byDay: 'TH', short: 'T', name: 'Thursday' },
  { value: 5, byDay: 'FR', short: 'F', name: 'Friday' },
  { value: 6, byDay: 'SA', short: 'S', name: 'Saturday' },
] as const;

const ANCHOR_DATE = '20240303'; // a Sunday — anchors the BYDAY week (scio3 SIMPLE_RRULE_ANCHOR_DATE)
const DEFAULT_FROM = '04:00';
const DEFAULT_TO = '23:59';

/** Built-in choices that are not saved Schedule documents. */
export const BUILTIN_OPS_IDS = new Set(['', 'none', 'default', 'simple']);

export interface SimpleSchedule {
  days: number[];
  /** 24h "HH:mm". */
  from: string;
  to: string;
}

/** "7:5" / "07:05" / junk → "07:05" (junk → fallback). */
function normalizeClock(raw: string | undefined, fallback: string): string {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec((raw ?? '').trim());
  if (!m) return fallback;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!(h >= 0 && h <= 23 && min >= 0 && min <= 59)) return fallback;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function buildSimpleRRule(s: SimpleSchedule): { rrule: string; range: string } {
  const days = s.days.length ? s.days : WEEK_DAYS.map((d) => d.value);
  const byDay = WEEK_DAYS.filter((d) => days.includes(d.value))
    .map((d) => d.byDay)
    .join(',');
  const from = normalizeClock(s.from, DEFAULT_FROM);
  const to = normalizeClock(s.to, DEFAULT_TO);
  return {
    rrule: `DTSTART:${ANCHOR_DATE}T${from.replace(':', '')}00Z\nRRULE:FREQ=WEEKLY;BYDAY=${byDay};INTERVAL=1`,
    range: `${from};${to}`,
  };
}

/** Stored rrule + range → editor values. Handles the legacy `{RRULE}|{HH:mm};{HH:mm}` combined form. */
export function parseSimpleRRule(rruleRaw: unknown, rangeRaw: unknown): SimpleSchedule {
  let rrule = typeof rruleRaw === 'string' ? rruleRaw : '';
  let range = typeof rangeRaw === 'string' ? rangeRaw : '';
  if (rrule.includes('|')) {
    const [r, rangePart] = rrule.split('|');
    rrule = r ?? '';
    if (!range && rangePart) range = rangePart;
  }
  const days: number[] = [];
  const byDay = /BYDAY=([A-Z,]+)/.exec(rrule)?.[1];
  for (const code of byDay?.split(',') ?? []) {
    const d = WEEK_DAYS.find((w) => w.byDay === code);
    if (d) days.push(d.value);
  }
  const [from, to] = range.split(';');
  return {
    days: days.length ? days : WEEK_DAYS.map((d) => d.value),
    from: normalizeClock(from, DEFAULT_FROM),
    to: normalizeClock(to, DEFAULT_TO),
  };
}

function to12h(clock: string): string {
  const [h = 0, m = 0] = clock.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour).padStart(2, '0')}:${String(m).padStart(2, '0')}${period}`;
}

/** scio1/scio3 wording: "Every day 04:00AM - 11:59PM" / "Every Monday, Friday 09:00AM - 05:00PM". */
export function formatSimpleSchedule(s: SimpleSchedule): string {
  const days =
    s.days.length === 7
      ? 'Every day'
      : `Every ${[...s.days]
          .sort((a, b) => a - b)
          .map((d) => WEEK_DAYS[d]?.name ?? '')
          .join(', ')}`;
  return `${days} ${to12h(s.from)} - ${to12h(s.to)}`;
}

/**
 * Feature patch for an ops-schedule choice (scio3 `opsScheduleFeaturePatch`): RRULE/range travel ONLY
 * with 'simple'; every other id carries just `scheduleOpsId` and leaves any stored RRULE/range alone.
 */
export function opsScheduleFeaturePatch(
  id: string,
  simple?: SimpleSchedule,
): { feature: Record<string, unknown> } {
  const feature: Record<string, unknown> = { scheduleOpsId: id };
  if (id === 'simple' && simple) {
    const { rrule, range } = buildSimpleRRule(simple);
    feature.scheduleOperationalRRule = rrule;
    feature.scheduleOperationalRange = range;
  }
  return { feature };
}
